<template>
    <div
        v-if="visibleGroups.length"
        class="OrgOpportunitiesCount -mx-4 grid grid-cols-1 justify-between gap-y-2 sm:grid-cols-2 sm:divide-gray-400 sm:dark:divide-default lg:grid-cols-3 lg:divide-x"
    >
        <OrgOpportunitiesCountCard
            v-for="(group, index) in visibleGroups"
            :key="index"
            :actions="group.actions"
            class="px-4"
            :icon="group.icon"
            :items="group.items"
            :scroll-to-hash="group.anchor"
            :show-actions="showActions"
            :title="group.title"
        />
    </div>
</template>

<script setup lang="ts">
import type { Job } from '#shared/types/Job'
import type { Project } from '#shared/types/Project'
import type { Skill } from '#shared/types/Skill'
import { VisibilityType } from '#shared/types/visibility'
import { isPublicVolunteeringWeekDraftProject } from '#shared/utils/publicProjectFilters'
import OrgOpportunitiesCountCard from '@/components/orgs/OrgOpportunitiesCountCard.vue'

const props = withDefaults(defineProps<{
    orgId: string
    skills?: Skill[]
    projects?: Project[]
    jobs?: Job[]
    showActions?: boolean
}>(), {
    skills: () => [],
    projects: () => [],
    jobs: () => [],
    showActions: false
})

const opportunityGroups = computed(() => [
    {
        title: 'Pracovné ponuky',
        icon: 'work-48',
        items: props.jobs,
        anchor: 'active-jobs' as const,
        actions: {
            add: { name: 'profile-jobs-create', query: { org: props.orgId } },
            settings: {
                name: 'profile-activity',
                query: {
                    owner: props.orgId,
                    type: 'job'
                }
            }
        }
    },
    {
        title: 'Dobrovoľnícke projekty',
        icon: 'projects-48',
        items: props.projects,
        anchor: 'active-projects' as const,
        actions: {
            add: { name: 'profile-projects-create', query: { org: props.orgId } },
            settings: {
                name: 'profile-activity',
                query: {
                    owner: props.orgId,
                    type: 'volunteering'
                }
            }
        }
    },
    {
        title: 'Vzdelávacie príležitosti',
        icon: 'course-48',
        items: props.skills,
        anchor: 'active-skills' as const,
        actions: {
            add: { name: 'profile-skills-create', query: { org: props.orgId } },
            settings: {
                name: 'profile-activity',
                query: {
                    owner: props.orgId,
                    type: 'education'
                }
            }
        }
    }
])

// Visitors only see groups with something they can open right now ("aktuálne 0" cards are noise).
// Members managing the org (showActions) keep all three so they can add the first opportunity.
const visibleGroups = computed(() => {
    if (props.showActions) return opportunityGroups.value
    return opportunityGroups.value.filter((group) =>
        group.items.some((item) => item.visibility === VisibilityType.published || isPublicVolunteeringWeekDraftProject(item))
    )
})
</script>
